var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * State change entry format:
 * {
 *   type: string,           // e.g., "text_0", "annotations_1", "dice_123", etc.
 *   id: string | number,    // ID of the item being changed
 *   propertyName: string,   // e.g., "position", "color", "text", "delete", "duplicate"
 *   old: any,               // Previous value (can be object, string, number, etc.)
 *   new?: any               // New value (optional, for updates)
 * }
 */
var StateManager = /** @class */ (function () {
    function StateManager() {
        this.history = []; // Array of state snapshots
        this.currentIndex = -1; // Current position in history
        this.maxHistorySize = 50; // Maximum number of history entries
    }
    /**
     * Save a state change or batch of state changes
     * @param stateChange - Single state change or array of state changes
     */
    StateManager.prototype.save = function (stateChange) {
        var changes = Array.isArray(stateChange) ? stateChange : [stateChange];
        // Remove any future history if we're not at the end (user made a change after undo)
        if (this.currentIndex < this.history.length - 1) {
            this.history = this.history.slice(0, this.currentIndex + 1);
        }
        // Add the new state change(s) to history
        this.history.push(__spreadArray([], changes, true));
        // Update current index
        this.currentIndex = this.history.length - 1;
        // console.log('[Save]', { saved: changes, currentIndex: this.currentIndex, history: this.history });
        // Limit history size
        if (this.history.length > this.maxHistorySize) {
            this.history.shift();
            this.currentIndex = this.history.length - 1;
        }
    };
    /**
     * Get the last saved state changes for undo
     * @returns Array of state changes that can be used to undo, or null if nothing to undo
     */
    StateManager.prototype.undo = function () {
        if (this.currentIndex < 0) {
            return null; // Nothing to undo
        }
        var stateChanges = this.history[this.currentIndex];
        this.currentIndex--;
        // console.log('[Undo]', { applying: stateChanges, remainingIndex: this.currentIndex, history: this.history });
        return stateChanges;
    };
    /**
     * Get the next state changes for redo
     * @returns Array of state changes that can be used to redo, or null if nothing to redo
     */
    StateManager.prototype.redo = function () {
        if (this.currentIndex >= this.history.length - 1) {
            return null; // Nothing to redo
        }
        this.currentIndex++;
        var stateChanges = this.history[this.currentIndex];
        // console.log('[Redo]', { applying: stateChanges, currentIndex: this.currentIndex, history: this.history });
        return stateChanges;
    };
    /**
     * Reset the state manager (clear all history)
     */
    StateManager.prototype.reset = function () {
        this.history = [];
        this.currentIndex = -1;
    };
    /**
     * Get all saved state changes
     * @returns Array of all state change arrays
     */
    StateManager.prototype.getAllHistory = function () {
        return __spreadArray([], this.history, true);
    };
    /**
     * Get the current state changes at the current index
     * @returns Current state changes or null
     */
    StateManager.prototype.getCurrentState = function () {
        if (this.currentIndex < 0 || this.currentIndex >= this.history.length) {
            return null;
        }
        return __spreadArray([], this.history[this.currentIndex], true);
    };
    /**
     * Check if undo is available
     * @returns true if undo is possible
     */
    StateManager.prototype.canUndo = function () {
        return this.currentIndex >= 0;
    };
    /**
     * Check if redo is available
     * @returns true if redo is possible
     */
    StateManager.prototype.canRedo = function () {
        return this.currentIndex < this.history.length - 1;
    };
    /**
     * Get the current history index
     * @returns Current index in history
     */
    StateManager.prototype.getCurrentIndex = function () {
        return this.currentIndex;
    };
    /**
     * Update the `new` property of a specific history entry in-place.
     * Used to patch the "new" value of an existing undo entry rather than
     * pushing a fresh entry (e.g. updating text after a focus-out).
     */
    StateManager.prototype.updateEntryNew = function (historyIndex, type, id, propertyName, newValue) {
        if (historyIndex < 0 || historyIndex >= this.history.length)
            return false;
        var entry = this.history[historyIndex].find(function (e) { return e.type === type && String(e.id) === String(id) && e.propertyName === propertyName; });
        if (!entry)
            return false;
        entry.new = newValue;
        return true;
    };
    /**
     * Get the total number of history entries
     * @returns Total history count
     */
    StateManager.prototype.getHistoryCount = function () {
        return this.history.length;
    };
    /**
     * Set the maximum history size
     * @param size - Maximum number of history entries to keep
     */
    StateManager.prototype.setMaxHistorySize = function (size) {
        this.maxHistorySize = Math.max(1, size);
        // Trim history if it exceeds the new max size
        if (this.history.length > this.maxHistorySize) {
            var itemsToRemove = this.history.length - this.maxHistorySize;
            this.history = this.history.slice(itemsToRemove);
            this.currentIndex = this.history.length - 1;
        }
    };
    /**
     * Get an object by its index
     * @param index - Index of the object to get
     * @returns Object or null
     */
    StateManager.prototype.getObjectByIndex = function (index) {
        return this.history[index] ? this.history[index][0] : null;
    };
    /**
     * Get an object by its ID
     * @param id - ID of the object to get
     * @returns Object or null
     */
    StateManager.prototype.getObjectByID = function (type, id) {
        var data = this.history.find(function (entry) {
            return entry.some(function (e) { return e.type === type && String(e.id) === String(id); });
        });
        if (!data)
            return null;
        return data;
    };
    return StateManager;
}());
export { StateManager };
// Export a singleton instance for global use
export var stateManager = new StateManager();
